import { Box, Button, CheckBox, FormField, Grid, ResponsiveContext, TextInput } from "grommet";
import * as React from "react";
import { ImagePicker } from "../components/ImagePicker";
import { ResolveUsers } from "../organizations/IdentityAPI";
import AliasPhotoPicker from "./AliasPhotoPicker";
import AliasSplashPicker from "./AliasSplashPicker";
import { AliasViewModel, CreateAliasRequest, IdentityUser, removeAliasProfileBackground, UpdateAliasRequest } from "./AliasStore";
import AliasProfilePicker from "./AliasProfilePicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const background = <FontAwesomeIcon icon={["fad", "transporter-3"]}></FontAwesomeIcon>;

const AliasDetails = ({
    alias,
    isBusy,
    onChange,
    size = "medium",
    hasChanges = false,
}: {
    alias: AliasViewModel | CreateAliasRequest | UpdateAliasRequest;
    isBusy: boolean;
    onChange: Function;
    size?: string;
    hasChanges?: boolean;
}) => {
    const [name, setName] = React.useState(alias.name || "");
    const [photoUri, setPhotoUri] = React.useState(alias.photoUri || "");
    const [splashUri, setSplashUri] = React.useState(alias.splashUri || "");
    const [profileUri, setProfileUri] = React.useState(alias.profileUri || "");
    const [email, setEmail] = React.useState(alias.email || "");

    const [archived, setArchived] = React.useState(alias.archived || false);

    const [photoContent, setPhotoContent] = React.useState("");
    const [splashContent, setSplashContent] = React.useState("");
    const [profileContent, setProfileContent] = React.useState("");

    React.useEffect(() => {
        onChange({ name, photoUri, photoContent, splashUri, splashContent, profileContent, profileUri, email, archived });
    }, [name, photoUri, photoContent, splashUri, splashContent, profileContent, profileUri, email, archived]);

    let [identityUser, setIdentityUser] = React.useState({} as IdentityUser);

    const [showNames, setShowNames] = React.useState(false);

    React.useEffect(() => {
        ResolveUsers([alias.identityUserID]).then((res) => {
            if (res.length == 0) {
                setShowNames(false);
            } else {
                setIdentityUser(res[0]);
                setShowNames(true);
            }
        });
    }, [alias.identityUserID]);

    React.useEffect(() => {
        if (alias.photoUri && photoUri != alias.photoUri) {
            setPhotoUri(alias.photoUri);
        }
        if (alias.splashUri && splashUri != alias.splashUri) {
            setSplashUri(alias.splashUri);
        }
    }, [alias.photoUri, alias.splashUri]);

    const handlePhotoChange = ({ uri, content }) => {
        if (content) {
            setPhotoContent(content);
            setPhotoUri("");
        } else if (uri) {
            setPhotoUri(uri);
            setPhotoContent("");
        }
    };

    const handleSplashChange = ({ uri, content }) => {
        if (content) {
            setSplashContent(content);
            setSplashUri("");
        } else if (uri) {
            setSplashUri(uri);
            setSplashContent("");
        }
    };

    const handleProfileChange = ({ uri, content }) => {
        if (content) {
            setProfileContent(content);
            setProfileUri("");
        } else if (uri) {
            setProfileUri(uri);
            setProfileContent("");
        }
    };

    const [rerenderKey, setRerenderKey] = React.useState(0);
    const [loading, setLoading] = React.useState(false);

    const removeBackground = () => {
        setLoading(true);
        removeAliasProfileBackground((alias as AliasViewModel).id).then((res) => {
            if (res.success) {
                setRerenderKey(rerenderKey + 1);
            }
            setLoading(false);
        });
    };

    const responsiveSize = React.useContext(ResponsiveContext);

    return (
        <Box>
            <Grid columns={responsiveSize == "small" || size == "small" ? ["100%"] : ["1/2", "1/2"]} rows={["auto"]} gap="small">
                <Box fill={true}>
                    <FormField label="Alias">
                        <TextInput value={name} onChange={(e) => setName(e.target.value)} disabled={isBusy} />
                    </FormField>

                    <FormField label="Email">
                        <TextInput value={email} onChange={(e) => setEmail(e.target.value)} disabled={isBusy} />
                    </FormField>

                    <FormField label="Archived">
                        <CheckBox checked={archived} onChange={(e) => setArchived(e.target.checked)} disabled={isBusy} />
                    </FormField>
                </Box>
                <Box fill={true}>
                    {showNames && (
                        <>
                            <FormField label="First Name">
                                <TextInput value={identityUser.firstName || ""} readOnly />
                            </FormField>

                            <FormField label="Last Name">
                                <TextInput value={identityUser.lastName || ""} readOnly />
                            </FormField>
                        </>
                    )}
                </Box>
                <Box fill={true}>
                    <AliasProfilePicker key={rerenderKey} uri={profileUri} disabled={loading || isBusy} onSelect={handleProfileChange}>
                        {(alias as AliasViewModel).id && <Button icon={background} onClick={removeBackground} disabled={loading || isBusy || hasChanges}></Button>}
                    </AliasProfilePicker>
                </Box>
                {responsiveSize != "small" && size != "small" && (
                    <>
                        <Box fill={true}>
                            <AliasSplashPicker uri={splashUri} disabled={isBusy} onSelect={handleSplashChange}></AliasSplashPicker>
                        </Box>
                        <Box fill={true}>
                            <AliasPhotoPicker uri={photoUri} disabled={isBusy} onSelect={handlePhotoChange}></AliasPhotoPicker>
                        </Box>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default AliasDetails;
